/**
 * `06/ Parties` (Figma 12:5880) — `/countries/:cc/parties`, v19 D4.
 *
 * Projects and LIFETIME value per party (`COUNT(crfs)`, `SUM(crfs.amount)` in the one
 * country's one currency — an honest sum). The period is OPTIONAL here: with no `?period=`
 * the list is lifetime ("All time"), and a period narrows the list by last activity only —
 * the server's disclosure says so and is rendered. `parties.view` is the widest read key
 * (every seeded role holds it), so the money column is the one most often masked: Tech
 * Support and Developer read the marker, and their export carries the marker.
 *
 * ⚠ A MASKED SEAT CANNOT SORT BY MONEY. The server applies `sort=projects` instead and
 * answers `sort` with what it USED; the screen says so beside the control rather than
 * letting a sort control claim an order the rows are not in.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useCountryCode } from '../../auth/guards';
import { useSession } from '../../auth/session';
import { KpiTile } from '../dashboards/dataviz';
import { formatDateLong, formatMoney } from '../inquiries/format';
import { ServerAmount } from '../quotes/money';
import { PARTY_REPORT_SORTS, PARTY_SORT_LABEL, fetchPartiesReport, reportKeys } from './api';
import type { PartiesReportDto, PartyRowDto } from './api';
import { ReportFrame } from './ReportFrame';
import type { ReportColumn } from './ReportFrame';
import { useReportUrl } from './report-filters';

/**
 * ⚠ THE PARTY CELL IS A LINK AS OF v20 D2, and the CSV is deliberately NOT.
 *
 * The party call log (`features/party-calls/`) has no nav row — the sidebar registry is
 * an IA decision the owner rules on — so this cell is how a reader reaches a customer's
 * call history from a screen where they are already looking at the customer. The `csv`
 * projection stays the bare name: an export is data, and a `<Link>` has no text form. The
 * two halves of a column diverging here is the ReportFrame contract working, not a slip.
 */
const COLUMNS: readonly ReportColumn<PartyRowDto>[] = [
  {
    key: 'name',
    header: 'Party',
    cell: (row) => <PartyNameCell row={row} />,
    csv: (row) => row.name,
  },
  { key: 'city', header: 'City', width: 'w-[140px]', cell: (row) => row.city ?? <span className="text-fg-secondary">—</span>, csv: (row) => row.city ?? '—' },
  {
    key: 'contact',
    header: 'Phone / email',
    width: 'w-[220px]',
    cell: (row) =>
      row.phone === null && row.email === null ? (
        <span className="text-fg-secondary">—</span>
      ) : (
        <span>
          {row.phone ?? ''}
          {row.phone !== null && row.email !== null ? <span className="text-fg-secondary"> · </span> : ''}
          {row.email ?? ''}
        </span>
      ),
    csv: (row) => [row.phone, row.email].filter((part): part is string => part !== null).join(' · ') || '—',
  },
  { key: 'source', header: 'Source', width: 'w-[110px]', cell: (row) => row.source, csv: (row) => row.source },
  { key: 'status', header: 'Status', width: 'w-[100px]', cell: (row) => row.status, csv: (row) => row.status },
  { key: 'projects', header: 'Projects', width: 'w-[100px]', numeric: true, cell: (row) => row.projects, csv: (row) => String(row.projects) },
  {
    key: 'amount',
    header: 'Lifetime value',
    width: 'w-[150px]',
    numeric: true,
    cell: (row) => <ServerAmount value={row.amount} currency={row.currency} />,
    csv: (row) => formatMoney(row.amount, row.currency),
  },
  { key: 'last', header: 'Last activity', width: 'w-[130px]', cell: (row) => formatDateLong(row.last_activity), csv: (row) => formatDateLong(row.last_activity) },
];

/**
 * The customer's name, linked to their record. `useCountryCode()` rather than a prop so
 * the cell cannot be rendered under a country the shell is not in.
 *
 * ⚠ RE-POINTED (feat/party-referral-screen). v20 D2 aimed this at
 * `parties/:id/calls` because *"there is no party record page in this app"* — the panel
 * needed a routed home and this column was the reader's way to it. The record page now
 * exists, embeds that same call-history panel, and is where the referral is read and
 * set — so the name goes to the customer's home, exactly as an inquiry row's subject
 * goes to the inquiry record. The calls page stays mounted for deep links; the record's
 * "Full log" action reaches it in one click.
 */
function PartyNameCell({ row }: { row: PartyRowDto }): React.JSX.Element {
  const countryCode = useCountryCode();
  return (
    <Link
      to={`/countries/${countryCode}/parties/${row.party_id}`}
      className="font-medium text-indigo hover:underline"
      title={`Customer record for ${row.name}`}
    >
      {row.name}
    </Link>
  );
}

function Summary({ data }: { data: PartiesReportDto }): React.JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="parties-summary">
      <KpiTile label="Parties" value={data.summary.parties} detail={data.window === null ? 'All time' : 'Active in the window'} />
      <KpiTile label="With projects" value={data.summary.with_projects} detail="At least one document" />
    </div>
  );
}

export function PartiesReportPage(): React.JSX.Element {
  const countryCode = useCountryCode();
  const { can } = useSession();
  const canView = can(countryCode, 'parties.view');
  const canExport = can(countryCode, 'reports.export');
  const { partiesFilters, setParam } = useReportUrl();

  const query = useQuery({
    queryKey: reportKeys.parties(countryCode, partiesFilters),
    queryFn: ({ signal }) => fetchPartiesReport(countryCode, partiesFilters, signal),
    enabled: canView,
  });

  const requestedSort = partiesFilters.sort ?? '';
  const appliedSort = query.data?.sort;
  const sortFellBack = appliedSort !== undefined && requestedSort !== '' && appliedSort !== requestedSort;

  const controls = (
      <div className="flex items-center gap-2">
        <label className="text-body13 text-fg-secondary" htmlFor="report-parties-q">
          Search
        </label>
        <input
          id="report-parties-q"
          type="search"
          defaultValue={partiesFilters.q ?? ''}
          onBlur={(event) => setParam('q', event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') setParam('q', (event.target as HTMLInputElement).value);
          }}
          placeholder="Party name"
          className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
        />
        <label className="text-body13 text-fg-secondary" htmlFor="report-parties-sort">
          Sort
        </label>
        <select
          id="report-parties-sort"
          value={requestedSort}
          onChange={(event) => setParam('sort', event.target.value === '' ? null : event.target.value)}
          className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
        >
          <option value="">Default</option>
          {PARTY_REPORT_SORTS.map((value) => (
            <option key={value} value={value}>
              {PARTY_SORT_LABEL[value]}
            </option>
          ))}
        </select>
        {sortFellBack && appliedSort !== undefined ? (
          <span className="text-body13 text-fg-secondary" data-testid="sort-applied">
            Sorted by {PARTY_SORT_LABEL[appliedSort].toLowerCase()} — this account cannot sort by amounts.
          </span>
        ) : null}
      </div>
  );

  return (
    <ReportFrame
      title="Parties"
      testId="report-parties"
      refusal={
        canView
          ? null
          : {
              title: 'Parties is not available to this account',
              body: `Viewing parties in ${countryCode} needs the parties permission. Ask an administrator to widen your access.`,
            }
      }
      query={query}
      columns={COLUMNS}
      rowKey={(row) => row.party_id}
      noun={['party', 'parties']}
      summary={(data) => <Summary data={data as PartiesReportDto} />}
      extraControls={controls}
      periodAllowAll
      canExport={canExport}
      filename={`${countryCode.toLowerCase()}-report-parties.csv`}
      emptyTitle="No party matches these filters."
    />
  );
}
